/**
 * @file       Groups vuex store, used by the whole groups module.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import axios from "@/plugins/axios.js";

/**
 * Gets the default state.
 *
 * @return  {Object}  The default state.
 */
function getDefaultState() {
  return {
    teacherGroups: {
      status: null,
      data: { data: [] },
      error: null,
      filters: {
        group: null,
        teacher: null,
        name: null,
        grid: null,
        status: null,
        resourcesPerPage: 5,
        page: 1
      }
    },
    teacherGroup: {
      status: null,
      data: null,
      error: null
    },
    teacherStudnets: {
      status: null,
      data: { data: [] },
      error: null,
      filters: {
        group: null,
        teacher: null,
        deliberation: null,
        status: null,
        status_user_group: null,
        resourcesPerPage: 10,
        page: 1
      }
    },
    studentsArchived: {
      status: null,
      data: null,
      error: null
    },
    studentsGroup: {
      status: null,
      data: { data: [] },
      error: null,
      filters: {
        student: null,
        group: null,
        resourcesPerPage: 5,
        page: 1
      }
    }
  };
}

/**
 * Groups vuex actions.
 *
 * @type  {Object}
 */
const actions = {
  /**
   * Get teacher groups.
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getTeacherGroups: ({ commit, state }) => {
    const payload = Object.entries(state.teacherGroups.filters).reduce(
      (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
      {}
    );
    return new Promise((resolve, reject) => {
      commit("onGetTeacherGroups");
      axios
        .get(`teacher/group`, { params: payload })
        .then(response => {
          commit("onGetTeacherGroupsSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetTeacherGroupsError", error);
          reject(error);
        });
    });
  },

  /**
   * Update teacher groups.
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    payload          The payload
   *
   * @return  {Promise}
   */
  postTeacherGroup: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPostTeacherGroup");

      axios
        .post("teacher/group", payload)
        .then(response => {
          commit("onPostTeacherGroupSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPostTeacherGroupError", error);
          reject(error);
        });
    });
  },

  /**
   * Delete the teacher group.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  deleteTeacherGroup: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onDeleteTeacherGroup");
      axios
        .patch("teacher/group", params)
        .then(response => {
          commit("onDeleteTeacherGroupSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onDeleteTeacherGroupError", error);
          reject(error);
        });
    });
  },

  /**
   * Get teacher students.
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getTeacherStudnets: ({ commit, state }) => {
    const payload = Object.entries(state.teacherStudnets.filters).reduce(
      (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
      {}
    );
    return new Promise((resolve, reject) => {
      commit("onGetTeacherStudnets");
      axios
        .get(`teacher/group/filter`, { params: payload })
        .then(response => {
          commit("onGetTeacherStudnetsSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetTeacherStudnetsError", error);
          reject(error);
        });
    });
  },

  /**
   * Archive students.
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Function}  arg1.dispatch    Dispatch the action
   * @param   {Object}    params           The parameters
   *
   * @return  {Promise}
   */
  putStudentsArchived: ({ commit, dispatch }, params) => {
    const url = params.all ? "teacher/group/statusAll" : "teacher/group/status";
    return new Promise((resolve, reject) => {
      commit("onPutStudentsArchived");
      axios
        .put(url, params)
        .then(response => {
          commit("onPutStudentsArchivedSuccess", response);
          dispatch("getTeacherStudnets");
          resolve(response);
        })
        .catch(error => {
          commit("onPutStudentsArchivedError", error);
          reject(error);
        });
    });
  },

  /**
   * Get students group.
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getStudentsGroup: ({ commit, state }) => {
    const payload = Object.entries(state.studentsGroup.filters).reduce(
      (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
      {}
    );
    return new Promise((resolve, reject) => {
      commit("onGetStudentsGroup");
      axios
        .get(`student/group`, { params: payload })
        .then(response => {
          commit("onGetStudentsGroupSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetStudentsGroupError", error);
          reject(error);
        });
    });
  }
};

/**
 * Groups vuex mutations.
 *
 * @type  {Object}
 */
const mutations = {
  /**
   * Commit teacher group.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetTeacherGroups: state => {
    state.teacherGroups.status = "fetching";
    state.teacherGroups.error = {};
  },

  /**
   * Commit teacher groups success.
   *
   * @param  {Object}  state     The vuex state
   * @param  {Object}  arg2.data The data returned
   */
  onGetTeacherGroupsSuccess: (state, { data }) => {
    state.teacherGroups.status = "success";
    state.teacherGroups.error = null;
    state.teacherGroups.data = data;
  },

  /**
   * Commit teacher groups error.
   *
   * @param  {Object}  state     The vuex state
   * @param  {Object}  arg2.data The data returned
   */
  onGetTeacherGroupsError: (state, err) => {
    state.teacherGroups.status = "error";
    state.teacherGroups.error = err;
  },

  /**
   * Commit teacher groups filters.
   *
   * @param  {Object}  state     The vuex state
   * @param  {Object}  filters   The filters
   */
  setTeacherGroupsFilters: (state, filters) => {
    if (filters.reset) {
      state.teacherGroups.filters = {
        group: null,
        teacher: null,
        name: null,
        grid: null,
        status: null,
        resourcesPerPage: 5,
        page: 1
      };
    }
    if (filters.fields) {
      state.teacherGroups.filters = {
        ...state.teacherGroups.filters,
        ...filters.fields
      };
    }
  },

  /**
   * Commit set teacher group.
   *
   * @param  {Object}  state   The vuex state
   */
  onPostTeacherGroup: state => {
    state.teacherGroup.status = "fetching";
    state.teacherGroup.error = {};
  },

  /**
   * Commit teacher group success.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  arg1.data   The data
   */
  onPostTeacherGroupSuccess: (state, { data }) => {
    state.teacherGroup.status = "success";
    state.teacherGroup.error = null;
    state.teacherGroup.data = data;
  },

  /**
   * Commit teacher group error.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  err    The error
   */
  onPostTeacherGroupError: (state, err) => {
    state.teacherGroup.status = "error";
    state.teacherGroup.error = err;
  },

  /**
   * Commit delete teacher group.
   *
   * @param  {Object}  state   The vuex state
   */
  onDeleteTeacherGroup: state => {
    state.teacherGroup.status = "fetching";
    state.teacherGroup.error = {};
  },

  /**
   * Delete teacher students success.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  arg1.data   The data
   */
  onDeleteTeacherGroupSuccess: (state, { data }) => {
    state.teacherGroup.status = "success";
    state.teacherGroup.error = null;
    state.teacherGroup.data = data;
  },

  /**
   * Delete teacher group error.
   *
   * @param  {Object}  state    The vuex state
   * @param  {Object}  err      The error
   */
  onDeleteTeacherGroupError: (state, err) => {
    state.teacherGroup.status = "error";
    state.teacherGroup.error = err;
  },

  /**
   * Commit get teacher students.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetTeacherStudnets: state => {
    state.teacherStudnets.status = "fetching";
    state.teacherStudnets.error = {};
  },

  /**
   * Get teacher students success.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  arg1.data   The data
   */
  onGetTeacherStudnetsSuccess: (state, { data }) => {
    state.teacherStudnets.status = "success";
    state.teacherStudnets.error = null;
    state.teacherStudnets.data = data;
  },

  /**
   * Get teacher students error.
   *
   * @param  {Object}  state    The vuex state
   * @param  {Object}  err      The error
   */
  onGetTeacherStudnetsError: (state, err) => {
    state.teacherStudnets.status = "error";
    state.teacherStudnets.error = err;
  },

  /**
   * Set teacher students filters.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  filters     The filters
   */
  setTeacherStudnetsFilters: (state, filters) => {
    if (filters.reset) {
      state.teacherStudnets.filters = {
        group: null,
        teacher: null,
        deliberation: null,
        status: null,
        status_user_group: null,
        resourcesPerPage: 10,
        page: 1
      };
    }
    if (filters.fields) {
      state.teacherStudnets.filters = {
        ...state.teacherStudnets.filters,
        ...filters.fields
      };
    }
  },

  /**
   * Commit put students archived.
   *
   * @param  {Object}  state   The vuex state
   */
  onPutStudentsArchived: state => {
    state.studentsArchived.status = "fetching";
    state.studentsArchived.error = {};
  },

  /**
   * Put students archived success.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  arg1.data   The data
   */
  onPutStudentsArchivedSuccess: (state, { data }) => {
    state.studentsArchived.status = "success";
    state.studentsArchived.error = null;
    state.studentsArchived.data = data;
  },

  /**
   * Put students archived error.
   *
   * @param  {Object}  state    The vuex state
   * @param  {Object}  err      The error
   */
  onPutStudentsArchivedError: (state, err) => {
    state.studentsArchived.status = "error";
    state.studentsArchived.error = err;
  },

  /**
   * Commit get students group.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetStudentsGroup: state => {
    state.studentsGroup.status = "fetching";
    state.studentsGroup.error = {};
  },

  /**
   * Get students group success.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  arg1.data   The data
   */
  onGetStudentsGroupSuccess: (state, { data }) => {
    state.studentsGroup.status = "success";
    state.studentsGroup.error = null;
    state.studentsGroup.data = data.data;
  },

  /**
   * Get students group error.
   *
   * @param  {Object}  state    The vuex state
   * @param  {Object}  err      The error
   */
  onGetStudentsGroupError: (state, err) => {
    state.studentsGroup.status = "error";
    state.studentsGroup.error = err;
  },

  /**
   * Set students group filters.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  filters     The filters
   */
  setStudentsGroupFilters: (state, filters) => {
    if (filters.reset) {
      state.studentsGroup.filters = {
        student: null,
        group: null,
        resourcesPerPage: 5,
        page: 1
      };
    }
    if (filters.fields) {
      state.studentsGroup.filters = {
        ...state.studentsGroup.filters,
        ...filters.fields
      };
    }
  }
};

/**
 * Export the store.
 *
 * @type  {Object}
 */
export default {
  namespaced: true,
  state: getDefaultState(),
  actions,
  mutations
};
